import { useState, useEffect } from "react";
import {
  Dialog,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Divider,
  IconButton,
} from "@mui/material";
import DateRangeIcon from "@mui/icons-material/DateRange";
import CircleIcon from "@mui/icons-material/Circle";
import CloseIcon from "@mui/icons-material/Close";
import {
  db,
  getDocs,
  collection,
  query,
  where,
  orderBy,
} from "../firebaseConfig";

const DateSelectorDialog = ({
  open,
  onClose,
  selectedJobs = [],
  setSelectedJobs,
  theme = "light",
  confirmationEmailData,
}) => {
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    if (
      !open ||
      !confirmationEmailData?.jobData?.bookedStart ||
      !confirmationEmailData?.jobData?.client
    ) {
      return;
    }

    const fetchJobs = async () => {
      try {
        const currentJobDate = new Date(
          confirmationEmailData.jobData.bookedStart
        );
        currentJobDate.setHours(1);
        currentJobDate.setMinutes(0);

        const q = query(
          collection(db, "JobData"),
          where("bookedStart", ">", currentJobDate.toISOString()),
          where("client", "==", confirmationEmailData.jobData.client),
          orderBy("bookedStart", "asc")
        );

        const snapshot = await getDocs(q);
        const jobsData = snapshot.docs
          .map((job) => {
            const tmp = job.data();
            tmp.id = job.id;
            return {
              data: tmp,
              label: parseJobDate(tmp),
            };
          })
          .filter((job) => job.data.id !== confirmationEmailData.jobData.id); // Exclude current job

        setJobs(jobsData);
      } catch (error) {
        console.error("Error fetching jobs:", error);
        setJobs([]);
      }
    };

    fetchJobs();
  }, [open, confirmationEmailData]);

  const parseJobDate = (jobData) => {
    try {
      let studioName;
      if (jobData?.studio === "Studio 1") studioName = "Studio 401";
      if (jobData?.studio === "Studio 2A") studioName = "Studio 403";
      if (jobData?.studio === "Studio 2B") studioName = "Studio 403";
      if (jobData?.studio === "Studio 2C") studioName = "Studio 403";
      if (jobData?.studio === "Studio 3") studioName = "Studio 302";
      if (jobData?.studio === "Studio 4") studioName = "Studio 301";

      const date = new Date(jobData?.bookedStart);
      const weekDay = parseWeekDay(date.getDay());
      const day = parseDay(date.getDate());
      const month = parseMonth(date.getMonth());
      const jobType = jobData?.jobTypeFull
        ?.replace(/ \(.*\)/, "")
        .toUpperCase();
      const dayType = jobData?.dayType?.toUpperCase();

      const lifeRes = `${weekDay} ${day} ${month} ${jobType} ${
        jobData?.multipleDays || ""
      }${
        jobData?.comments ? ` ${jobData.comments}` : ""
      } ${dayType} ${formatAMPM(date)} - ${formatAMPM(
        new Date(jobData?.bookedFinish)
      )} - ${studioName}`;
      const ghostRes = `${weekDay} ${day} ${month} ${jobType}${
        jobData?.multipleDays ? ` ${jobData.multipleDays}` : ""
      }${jobData?.comments ? ` ${jobData.comments}` : ""} x${
        jobData?.imagesScheduledToBeShot || "0"
      }`;

      let res;
      if (jobData?.jobTypeFull === "Creative Life (-LC)") res = lifeRes;
      else if (jobData?.jobTypeFull === "Life (-L)") res = lifeRes;
      else if (jobData?.jobTypeFull === "Video (-V)") res = lifeRes;
      else res = ghostRes;

      return res;
    } catch (e) {
      console.log(e);
      console.log(jobData);
      return jobData?.jobName || "Unknown Job";
    }
  };

  const formatAMPM = (date) => {
    var hours = date.getHours();
    var minutes = date.getMinutes();
    var ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    minutes = minutes < 10 ? "0" + minutes : minutes;
    var strTime = hours + ":" + minutes + ampm;
    return strTime;
  };

  const parseMonth = (month) => {
    if (month === 0) return "JANUARY";
    if (month === 1) return "FEBRUARY";
    if (month === 2) return "MARCH";
    if (month === 3) return "APRIL";
    if (month === 4) return "MAY";
    if (month === 5) return "JUNE";
    if (month === 6) return "JULY";
    if (month === 7) return "AUGUST";
    if (month === 8) return "SEPTEMBER";
    if (month === 9) return "OCTOBER";
    if (month === 10) return "NOVEMBER";
    if (month === 11) return "DECEMBER";
  };

  const parseDay = (i) => {
    var j = i % 10,
      k = i % 100;
    if (j === 1 && k !== 11) {
      return i + "ST";
    }
    if (j === 2 && k !== 12) {
      return i + "ND";
    }
    if (j === 3 && k !== 13) {
      return i + "RD";
    }
    return i + "TH";
  };

  const parseWeekDay = (day) => {
    if (day === 0) return "SUNDAY";
    if (day === 1) return "MONDAY";
    if (day === 2) return "TUESDAY";
    if (day === 3) return "WEDNESDAY";
    if (day === 4) return "THURSDAY";
    if (day === 5) return "FRIDAY";
    if (day === 6) return "SATURDAY";
  };

  const getStatusColor = (confirmationStatus) => {
    switch (confirmationStatus) {
      case "confirmed":
        return "#4caf50"; // Green
      case "pending":
        return "#ff9800"; // Orange
      default:
        return "#f44336"; // Red
    }
  };
  const handleJobToggle = (jobToToggle) => {
    setSelectedJobs((prev) => {
      // Check if job is already selected by ID
      const existingJobIndex = prev.findIndex(
        (job) => job.id === jobToToggle.id
      );
      if (existingJobIndex !== -1) {
        return prev.filter((job) => job.id !== jobToToggle.id);
      } else {
        return [...prev, jobToToggle];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedJobs.length === jobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(
        jobs.map((job) => ({ id: job.data.id, label: job.label, ...job.data }))
      );
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          style: {
            backgroundColor: theme === "dark" ? "#2c2c2c" : "#ffffff",
            color: theme === "dark" ? "#ffffff" : "#000000",
          },
        },
      }}
    >
      <div
        style={{
          width: 500,
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme === "dark" ? "#2c2c2c" : "#ffffff",
          color: theme === "dark" ? "#ffffff" : "#000000",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: theme === "dark" ? "#ffffff" : "#000000",
              "&:hover": {
                backgroundColor: theme === "dark" ? "#404040" : "#f5f5f5",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <DateRangeIcon
              sx={{ color: theme === "dark" ? "#ffffff" : "#000000" }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: theme === "dark" ? "#ffffff" : "#000000",
              }}
            >
              Select Additional Dates
            </Typography>
          </div>
          <div style={{ width: 40 }} /> {/* Spacer */}
        </div>
        {jobs.length === 0 ? (
          <>
            <div
              style={{
                textAlign: "center",
                padding: "40px 0",
                color: theme === "dark" ? "#b0b0b0" : "#666666",
                minHeight: 200,
              }}
            >
              <Typography variant="body1">
                No additional bookings found for this client.
              </Typography>
            </div>

            <div
              style={{
                marginTop: 16,
                padding: 16,
                backgroundColor: theme === "dark" ? "#1e3a5f" : "#e3f2fd",
                borderRadius: 4,
                minHeight: 48,
              }}
            >
              <Typography
                variant="body2"
                style={{
                  color: theme === "dark" ? "#90caf9" : "#1976d2",
                  fontWeight: "bold",
                }}
              >
                0 dates selected
              </Typography>
            </div>
          </>
        ) : (
          <>
            <Typography
              variant="body2"
              style={{
                marginBottom: 16,
                color: theme === "dark" ? "#b0b0b0" : "#666666",
              }}
            >
              Select additional bookings to include in this email:
            </Typography>

            <div style={{ marginBottom: 16 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSelectAll}
                sx={{
                  textTransform: "none",
                  color: theme === "dark" ? "#90caf9" : "#1976d2",
                  borderColor: theme === "dark" ? "#90caf9" : "#1976d2",
                  "&:hover": {
                    backgroundColor: theme === "dark" ? "#404040" : "#f5f5f5",
                    borderColor: theme === "dark" ? "#90caf9" : "#1976d2",
                  },
                }}
              >
                {selectedJobs.length === jobs.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
            </div>

            <Divider
              sx={{
                mb: 2,
                borderColor: theme === "dark" ? "#404040" : "#e0e0e0",
              }}
            />

            <FormGroup>
              {jobs.map((job) => (
                <FormControlLabel
                  key={job.label}
                  control={
                    <Checkbox
                      checked={selectedJobs.some(
                        (selectedJob) => selectedJob.id === job.data.id
                      )}
                      onChange={() =>
                        handleJobToggle({
                          id: job.data.id,
                          label: job.label,
                          ...job.data,
                        })
                      }
                      sx={{
                        color: theme === "dark" ? "#90caf9" : "#1976d2",
                        "&.Mui-checked": {
                          color: theme === "dark" ? "#90caf9" : "#1976d2",
                        },
                      }}
                    />
                  }
                  label={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flex: 1,
                      }}
                    >
                      <CircleIcon
                        sx={{
                          fontSize: 12,
                          color: getStatusColor(job.data.confirmationStatus),
                        }}
                      />
                      <Typography
                        variant="body2"
                        style={{
                          color: theme === "dark" ? "#ffffff" : "#000000",
                          fontFamily: "monospace",
                          fontSize: "0.9rem",
                          flex: 1,
                        }}
                      >
                        {job.label}
                      </Typography>
                    </div>
                  }
                  sx={{
                    mb: 1,
                    "& .MuiFormControlLabel-label": {
                      flex: 1,
                    },
                  }}
                />
              ))}
            </FormGroup>

            <div
              style={{
                marginTop: 16,
                padding: 16,
                backgroundColor: theme === "dark" ? "#1e3a5f" : "#e3f2fd",
                borderRadius: 4,
                minHeight: 48,
              }}
            >
              <Typography
                variant="body2"
                style={{
                  color: theme === "dark" ? "#90caf9" : "#1976d2",
                  fontWeight: "bold",
                }}
              >
                {selectedJobs.length} date{selectedJobs.length !== 1 ? "s" : ""}{" "}
                selected
              </Typography>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default DateSelectorDialog;
